import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Slider,
  FormControlLabel,
  Checkbox,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PartnerPriceSection from "layouts/pages/servicelists/PartnerPriceSection";
import saasService from "services/saasService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

const productConfigs = {
  'backup-now': {
    features: {
      fileBackup: "Endpoint File Sicherung (5€/Endpoint)",
      imageBackup: "Endpoint Image Sicherung Windows/Linux (10€/Endpoint)",
      winServerBackup: "Windows Server System Setting Backup (5€/Server)",
      hyperVBackup: "Hyper-V VM Backup (5€/VM)",
      vmwareBackup: "VMware VM Backup (7€/VM)",
      exchangeBackup: "Lokales MS Exchange Backup (2€/Account)",
      office365Backup: "Office 365 Account Backup (4€/Account)",
      sqlBackup: "SQL Backup (3€/Datenbank)"
    }
  },
  'office-365': {
    plans: [
      { 
        value: 'business-basic-eea', 
        label: 'Microsoft 365 Business Basic EEA (no Teams)', 
        description: 'Web-Versionen von Office-Apps ohne Teams',
        monthlyPrice: 5.75,
        yearlyPrice: 55.75
      },
      { 
        value: 'business-basic', 
        label: 'Microsoft 365 Business Basic', 
        description: 'Web-Versionen von Office-Apps',
        monthlyPrice: 7.10,
        yearlyPrice: 69.50
      },
      { 
        value: 'business-standard-eea', 
        label: 'Microsoft 365 Business Standard EEA (no Teams)', 
        description: 'Desktop-Versionen inklusive ohne Teams',
        monthlyPrice: 12.30,
        yearlyPrice: 118.50
      },
      { 
        value: 'business-standard', 
        label: 'Microsoft 365 Business Standard', 
        description: 'Desktop-Versionen inklusive',
        monthlyPrice: 14.20,
        yearlyPrice: 140.50
      },
      { 
        value: 'business-premium', 
        label: 'Microsoft 365 Business Premium', 
        description: 'Zusätzliche Sicherheitsfunktionen und weitere Features',
        monthlyPrice: 26.00,
        yearlyPrice: 251.00
      }
    ],
    userConfig: {
      min: 1,
      max: 500,
      step: 1
    },
    billingOptions: [
      { value: 'monthly', label: 'Monatliche Zahlung' },
      { value: 'yearly', label: 'Jährliche Zahlung' }
    ]
  },
  'tsplus': {
    licenses: {
      min: 3,
      max: 100,
      step: 1
    },
    editions: [
      { value: 'desktop', label: 'Desktop Edition', description: 'Grundlegende Remote-Desktop-Funktionen', price: 4.50 },
      { value: 'web-mobile', label: 'Web Mobile Edition', description: 'Erweiterte Web- und Mobile-Funktionen', price: 6.50 },
      { value: 'enterprise', label: 'Enterprise Edition', description: 'Vollständige Funktionalität für Unternehmen', price: 8.50 }
    ],
    contractPeriod: '12', // 1 Jahr Mindestvertragslaufzeit
    features: {
      premiumSupport: "Premium Support 24/7",
      secureGateway: "Secure Gateway",
      loadBalancing: "Load Balancing"
    }
  },
  'eset': {
    products: [
      { value: 'plan1', label: 'Plan 1', description: 'Basic Protection für Arbeitsplatzrechner' },
      { value: 'plan2', label: 'Plan 2', description: 'Advanced Protection mit Server-Schutz' },
      { value: 'plan3', label: 'Plan 3', description: 'Enterprise Protection mit allen Features' }
    ],
    userConfig: {
      min: 1,
      max: 500,
      step: 1
    }
  },
  'lastpass': {
    userConfig: {
      min: 1,
      max: 500,
      step: 1
    },
    features: {
      advancedReporting: "Erweiterte Berichte",
      apiAccess: "API-Zugang",
      customIntegrations: "Individuelle Integrationen"
    }
  },
  'nextcloud': {
    userConfig: {
      min: 1,
      max: 100,
      step: 1
    },
    storageOptions: [
      { value: 500, label: '500 GB', price: 9 },
      { value: 1000, label: '1 TB', price: 18 },
      { value: 2000, label: '2 TB', price: 28 },
      { value: 5000, label: '5 TB', price: 50 },
      { value: 10000, label: '10 TB', price: 80 },
      { value: 20000, label: '20 TB', price: 190 }
    ],
    features: {
      collaborationSuite: "Collaboration Suite",
      officeIntegration: "Office Integration",
      videoConferencing: "Video Conferencing"
    }
  }
};

function Configure({ productType, onBack }) {
  const { user } = useContext(AuthContext);
  const [serviceAlias, setServiceAlias] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [plan, setPlan] = useState('');
  const [userCount, setUserCount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [endCustomerPrice, setEndCustomerPrice] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [storageOption, setStorageOption] = useState(0);
  const [tsplusEdition, setTsplusEdition] = useState('');
  const config = productConfigs[productType];

  useEffect(() => {
    if (productType === 'eset') {
      setPlan('plan1');
      setUserCount(config.userConfig.min);
    } else if (productType === 'lastpass') {
      setUserCount(config.userConfig.min);
    } else if (productType === 'office-365') {
      setPlan('business-basic');
      setUserCount(config.userConfig.min);
    } else if (productType === 'tsplus') {
      // Setze für TSPlus den Minimalwert und die Standard-Edition
      const minLicenses = config.licenses ? config.licenses.min : 3;
      setUserCount(minLicenses);
      setTsplusEdition('desktop'); // Desktop Edition als Standard
    } else if (productType === 'nextcloud') {
      setUserCount(config.userConfig.min); // Mindestens 1 Benutzer für Nextcloud
      setStorageOption(config.storageOptions[0].value); // Setze den kleinsten Speicherwert als Standard
    }
  }, [productType, config]);

  // Berechne den Preis wenn sich relevante Werte ändern
  useEffect(() => {
    const calculatePrice = async () => {
      let basePrice = 0;
      
      // Berechne den Basispreis
      switch(productType) {
        case 'eset':
          basePrice = userCount * (plan === 'plan1' ? 4 : plan === 'plan2' ? 6 : 9);
          break;
        case 'lastpass':
          basePrice = userCount * 6.5;
          break;
        case 'office-365':
          const selectedPlan = config.plans.find(p => p.value === plan);
          if (selectedPlan) {
            const pricePerUser = billingPeriod === 'monthly' 
              ? selectedPlan.monthlyPrice 
              : selectedPlan.yearlyPrice / 12; // Convert yearly price to monthly equivalent
            basePrice = userCount * pricePerUser;
          }
          break;
        case 'backup-now':
          basePrice = 10; // Basispreis
          break;
        case 'tsplus':
          const selectedEdition = config.editions.find(edition => edition.value === tsplusEdition);
          if (selectedEdition) {
            basePrice = userCount * selectedEdition.price;
          }
          break;
        case 'nextcloud':
          const selectedStorage = config.storageOptions.find(option => option.value === storageOption);
          if (selectedStorage) {
            basePrice = selectedStorage.price;
          }
          break;
        default:
          basePrice = 0;
      }
      
      const isPartner = user?.isPartner === true;

      // Wende den Rabatt an, außer bei Microsoft M365
      if (isPartner && productType !== 'office-365') {
        // 20% Rabatt für Partner
        setOriginalPrice(basePrice);
        setTotalPrice(basePrice * 0.8);
        setDiscountApplied(true);
      } else {
        setTotalPrice(basePrice);
        setOriginalPrice(null);
        setDiscountApplied(false);
      }
    };
    
    calculatePrice();
  }, [productType, userCount, plan, billingPeriod, storageOption, tsplusEdition, user?.isPartner]);

  const getUserLimits = () => {
    if (productType === 'eset' || productType === 'lastpass' || productType === 'nextcloud') {
      return config.userConfig;
    } else if (productType === 'office-365') {
      return config.userConfig;
    } else if (productType === 'tsplus') {
      return config.licenses;
    }
    return { min: 1, max: 100, step: 1 }; // Standardwerte
  };

  const handleOrder = async () => {
    if (!agreedToTerms) {
      Swal.fire({
        icon: 'warning',
        title: 'Achtung',
        text: 'Bitte stimmen Sie den AGBs zu, um fortzufahren.',
        confirmButtonColor: '#f8bb86',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!serviceAlias.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Achtung',
        text: 'Bitte geben Sie einen Service-Alias ein.',
        confirmButtonColor: '#f8bb86',
        confirmButtonText: 'OK'
      });
      return;
    }

    try {
      // Erstelle das korrekte Konfigurationsobjekt je nach Produkttyp
      let configuration = {
        serviceName: serviceAlias,
        renewalPeriod: 1
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        configuration.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      // Füge die richtigen Parameter basierend auf dem Produkttyp hinzu
      switch (productType) {
        case 'eset':
          configuration = {
            ...configuration,
            devices: userCount,
            plan: plan || undefined
          };
          break;
        case 'office-365':
          configuration = {
            ...configuration,
            licenses: userCount,
            plan: plan || undefined,
            billingPeriod: billingPeriod
          };
          break;
        case 'lastpass':
          configuration = {
            ...configuration,
            users: userCount
          };
          break;
        case 'tsplus':
          configuration = {
            ...configuration,
            users: userCount,
            edition: tsplusEdition,
            contractPeriod: config.contractPeriod
          };
          break;
        case 'nextcloud':
          configuration = {
            ...configuration,
            storage: storageOption
          };
          break;
        case 'backup-now':
          // Backup-Now hat einen festen Preis
          break;
        default:
          break;
      }
      
      const response = await saasService.orderService(productType, configuration, endCustomerPrice);

      if (response) {
        Swal.fire({
          icon: 'success',
          title: 'Erfolg!',
          text: 'Ihre Bestellung wurde erfolgreich aufgegeben!',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'OK'
        }).then(() => {
          window.location.href = "/saas/services";
        });
      }
    } catch (error) {
      console.error("Fehler bei der Bestellung:", error);
      Swal.fire({
        icon: 'error',
        title: 'Fehler',
        text: error.message || 'Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  return (
    <SoftBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
            <SoftTypography variant="h5" fontWeight="medium" mb={2} color="dark">
              {productType.toUpperCase()} Konfiguration
            </SoftTypography>
            <TextField
              label="Service-Alias"
              value={serviceAlias}
              onChange={(e) => setServiceAlias(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#8b0000'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#8b0000'
                }
              }}
            />
          </SoftBox>
        </Grid>

        {(productType === 'eset' || productType === 'office-365') && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Plan auswählen</InputLabel>
              <Select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                label="Plan auswählen"
              >
                {productType === 'eset' ? 
                  config.products.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} - {option.description}
                    </MenuItem>
                  )) : 
                  config.plans.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} - {option.description}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {productType === 'office-365' && (
          <Grid item xs={12} mt={2}>
            <FormControl fullWidth>
              <InputLabel>Abrechnungszeitraum</InputLabel>
              <Select
                value={billingPeriod}
                onChange={(e) => setBillingPeriod(e.target.value)}
                label="Abrechnungszeitraum"
              >
                {config.billingOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {productType === 'nextcloud' && (
          <Grid item xs={12} mt={2}>
            <FormControl fullWidth>
              <InputLabel>Speicherplatz</InputLabel>
              <Select
                value={storageOption}
                onChange={(e) => setStorageOption(e.target.value)}
                label="Speicherplatz"
              >
                {config.storageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} - {option.price}€ pro Monat
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {productType === 'tsplus' && (
          <Grid item xs={12} mt={2}>
            <FormControl fullWidth>
              <InputLabel>Edition</InputLabel>
              <Select
                value={tsplusEdition}
                onChange={(e) => setTsplusEdition(e.target.value)}
                label="Edition"
              >
                {config.editions.map((edition) => (
                  <MenuItem key={edition.value} value={edition.value}>
                    {edition.label} - {edition.description} ({edition.price}€ pro Benutzer/Monat)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {(['eset', 'lastpass', 'office-365', 'tsplus'].includes(productType)) && (
          <Grid item xs={12}>
            <SoftTypography variant="h6" mb={1}>
              {productType === 'office-365' ? 'Anzahl Lizenzen' : productType === 'eset' ? 'Anzahl Geräte' : 'Anzahl Benutzer'}
            </SoftTypography>
            <Slider
              value={userCount}
              onChange={(_, value) => setUserCount(value)}
              min={getUserLimits().min}
              max={getUserLimits().max}
              step={getUserLimits().step}
              marks
              valueLabelDisplay="auto"
              aria-labelledby="user-count-slider"
            />
            <SoftTypography variant="body2" textAlign="center">
              {userCount} {productType === 'office-365' ? 'Lizenzen' : productType === 'eset' ? 'Geräte' : 'Benutzer'}
            </SoftTypography>
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                sx={{
                  color: '#8b0000',
                  '&.Mui-checked': {
                    color: '#8b0000',
                  },
                }}
              />
            }
            label={
              <SoftTypography variant="body2">
                Ich akzeptiere die{" "}
                <a href="/agb" target="_blank" rel="noopener noreferrer" style={{ color: '#8b0000' }}>
                  AGBs
                </a>{" "}
                und bestelle das Produkt kostenpflichtig.
              </SoftTypography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SoftBox display="flex" justifyContent="space-between">
            <Button 
              variant="outlined" 
              onClick={onBack}
              sx={{
                borderColor: '#8b0000',
                color: '#8b0000',
                '&:hover': {
                  borderColor: '#660000',
                  backgroundColor: 'rgba(139,0,0,0.04)'
                }
              }}
            >
              Zurück
            </Button>
            <Button
              variant="contained"
              onClick={handleOrder}
              disabled={!agreedToTerms || !serviceAlias.trim() || ((['eset', 'lastpass', 'office-365', 'tsplus'].includes(productType)) && userCount <= 0) || ((productType === 'eset' || productType === 'office-365') && !plan) || (productType === 'nextcloud' && !storageOption) || (productType === 'tsplus' && !tsplusEdition)}
              sx={{
                backgroundColor: '#8b0000',
                '&:hover': {
                  backgroundColor: '#660000'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#cccccc'
                }
              }}
            >
              Jetzt bestellen
            </Button>
          </SoftBox>
        </Grid>

        {/* Übersichtsblock */}
        <Grid item xs={12}>
          <SoftBox 
            mt={2} 
            p={3} 
            bgcolor="#f8f9fa"
            borderRadius="lg"
            border="1px solid #e9ecef"
            boxShadow="0 2px 12px 0 rgba(0,0,0,0.05)"
          >
            <SoftTypography variant="h6" fontWeight="medium" color="dark">
              Ihre Konfiguration
            </SoftTypography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SoftTypography variant="body2">
                  <strong>Service-Name:</strong> {serviceAlias}
                </SoftTypography>
              </Grid>

              {(productType === 'eset' || productType === 'office-365') && (
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    <strong>Ausgewählter Plan:</strong> {
                      productType === 'eset' 
                        ? config.products.find(p => p.value === plan)?.label || ''
                        : config.plans.find(p => p.value === plan)?.label || ''
                    }
                  </SoftTypography>
                </Grid>
              )}
              
              {productType === 'office-365' && (
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    <strong>Abrechnungszeitraum:</strong> {
                      config.billingOptions.find(p => p.value === billingPeriod)?.label || ''
                    }
                  </SoftTypography>
                </Grid>
              )}
              
              {productType === 'nextcloud' && (
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    <strong>Speicherplatz:</strong> {
                      config.storageOptions.find(p => p.value === storageOption)?.label || ''
                    }
                  </SoftTypography>
                </Grid>
              )}
              
              {productType === 'tsplus' && (
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    <strong>Edition:</strong> {
                      config.editions.find(e => e.value === tsplusEdition)?.label || ''
                    }
                  </SoftTypography>
                </Grid>
              )}
              
              {productType === 'tsplus' && (
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    <strong>Mindestvertragslaufzeit:</strong> 1 Jahr
                  </SoftTypography>
                </Grid>
              )}

              {(['eset', 'lastpass', 'office-365', 'tsplus'].includes(productType)) && (
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    <strong>{productType === 'office-365' ? 'Anzahl Lizenzen' : productType === 'eset' ? 'Anzahl Geräte' : 'Anzahl Benutzer'}:</strong> {userCount}
                  </SoftTypography>
                </Grid>
              )}

              <Grid item xs={12}>
                <SoftTypography variant="body2">
                  <strong>Produkt:</strong> {(() => {
                    switch(productType) {
                      case 'eset': return 'ESET';
                      case 'lastpass': return 'LastPass';
                      case 'office-365': return 'Microsoft 365';
                      case 'backup-now': return 'Backup Now';
                      case 'tsplus': return 'TSplus';
                      case 'nextcloud': return 'Nextcloud';
                      default: return productType;
                    }
                  })()}
                </SoftTypography>
              </Grid>

              {user?.isPartner && productType !== 'backup-now' && (
                <Grid item xs={12}>
                  <PartnerPriceSection
                    standardPrice={originalPrice || totalPrice}
                    purchasePrice={totalPrice}
                    endCustomerPrice={endCustomerPrice}
                    onChange={setEndCustomerPrice}
                  />
                </Grid>
              )}
              {productType !== 'backup-now' && (
                <Grid item xs={12}>
                  <SoftTypography variant="h6" color="dark" mt={2}>
                    {productType === 'office-365' && billingPeriod === 'yearly' ? (
                      <>
                        <div>Monatlicher Gesamtpreis: {totalPrice.toFixed(2)} €</div>
                        <div>Jährlicher Gesamtpreis: {(totalPrice * 12).toFixed(2)} €</div>
                      </>
                    ) : (
                      <>
                        {discountApplied ? (
                          <>
                            <div>
                              <span style={{ textDecoration: 'line-through', color: '#666', marginRight: '10px' }}>
                                {originalPrice.toFixed(2)} €
                              </span>
                              <span style={{ color: '#8b0000', fontWeight: 'bold' }}>
                                {totalPrice.toFixed(2)} € (20% Partner-Rabatt)
                              </span>
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                              {productType === 'office-365' ? 'Für Microsoft M365 wird kein Partner-Rabatt gewährt.' : ''}
                            </div>
                          </>
                        ) : (
                          <>Monatlicher Gesamtpreis: {totalPrice.toFixed(2)} €</>
                        )}
                      </>
                    )}
                  </SoftTypography>
                </Grid>
              )}
            </Grid>
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

Configure.propTypes = {
  productType: PropTypes.oneOf(['backup-now', 'office-365', 'tsplus', 'eset', 'lastpass', 'nextcloud']).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default Configure;